import { Button } from '@alkem/react-ui-button';
import classNames from 'classnames';
import { MouseEvent, PureComponent, ReactNode } from 'react';
import ReactModal from 'react-modal';
import './advanced-modal.scss';

const advancedStyle = {
  overlay: {
    zIndex: '1050',
    background: 'rgba(0, 0, 0, 0.5)',
  },
  content: {
    position: 'absolute',
    top: 'auto',
    left: 'auto',
    right: 'auto',
    bottom: 'auto',
    border: '0px',
    background: '#fff',
    overflow: 'auto',
    borderRadius: '0px',
    outline: 'none',
    padding: '0px',
    boxShadow: '0 3px 6px rgba(0,0,0,.16),0 3px 6px rgba(0,0,0,.23)',
  },
};

type Action = (e?: MouseEvent) => void;

export interface Props {
  // required
  title: string;
  children: ReactNode;

  // optional
  confirmButtonText?: string; // default "OK"
  confirmButtonWarning?: boolean;
  confirmDisabled?: boolean;
  closeButtonText?: string; // default "Cancel"
  isProcessing?: boolean;
  danger?: boolean; // header and confirm button in red
  warning?: boolean;
  hideFooter?: boolean;
  additionalFooterContent?: ReactNode; // only on the left side of the buttons
  className?: string;
  hideCloseButton?: boolean;
  withTransition: boolean;
  positionTop?: number;
  secondaryAction?: [string, Action];
  onConfirm?: Action;
  onClose?: Action;
}

class Modal extends PureComponent<Props> {
  public static defaultProps = {
    confirmButtonText: 'OK',
    closeButtonText: 'Cancel',
    modalStyle: 'default',
    withTransition: true,
    onConfirm() {
      // noop
    },
  };

  public render() {
    const {
      additionalFooterContent,
      children,
      className = '',
      closeButtonText,
      confirmButtonText,
      confirmButtonWarning,
      confirmDisabled,
      danger,
      warning,
      hideCloseButton,
      hideFooter,
      isProcessing,
      onClose,
      onConfirm,
      secondaryAction,
      positionTop,
      title,
      withTransition,
    } = this.props;

    const reactModalProps = {
      style: positionTop
        ? {
            ...advancedStyle,
            content: {
              ...advancedStyle.content,
              top: positionTop,
            },
          }
        : advancedStyle,
      isOpen: true,
      onRequestClose: onClose,
      ariaHideApp: false,
      portalClassName: withTransition ? 'ReactModalWithTransition' : '',
    };

    const modalClassName = classNames('AdvancedModal', className, {
      'Modal--withoutFooter': hideFooter,
      'Modal--danger': danger,
      'Modal--warning': warning,
    });

    return (
      <ReactModal {...reactModalProps}>
        <div className={modalClassName}>
          {/* MODAL HEADER */}
          <div className="Modal__header">
            <h3 className="Modal__title">{title}</h3>
            {onClose && (
              <i className="Modal__close mdi mdi-close" onClick={onClose} />
            )}
          </div>

          {/* MODAL BODY */}
          <div className="Modal__body">{children}</div>

          {/* MODAL FOOTER */}
          {hideFooter ? null : (
            <div className="Modal__footer">
              <div>
                {/* CLOSE BUTTON */}
                {onClose && !hideCloseButton && (
                  <Button
                    link
                    className="Modal__footerCloseButton"
                    onClick={onClose}
                  >
                    {closeButtonText}
                  </Button>
                )}

                {/* CLOSE BUTTON */}
                {secondaryAction && (
                  <Button
                    secondary
                    className="Modal__footerSecondaryButton"
                    onClick={secondaryAction[1]}
                  >
                    {secondaryAction[0]}
                  </Button>
                )}

                {/* CONFIRM BUTTON */}
                <Button
                  primary={!confirmButtonWarning}
                  warning={confirmButtonWarning}
                  className="Modal__footerConfirmButton"
                  onClick={onConfirm}
                  disabled={isProcessing || confirmDisabled}
                  displaySpinner={isProcessing}
                >
                  {confirmButtonText}
                </Button>
              </div>

              {/* LEFT SIDE FOOTER CONTENT */}
              <span className="Modal__additionalFooterContent">
                {additionalFooterContent}
              </span>
            </div>
          )}
        </div>
      </ReactModal>
    );
  }
}

export default Modal;
